<!--
 * @FileDescription: 权限管理页面
 * @Author: 陈文景
 * @Date: 2022-09-22 14:32:37
 * @LastEditors: 最后更新作者
 * @LastEditTime: 最后更新时间
-->
<template>
  <page-skeleton>
    <template slot="header-left">
      <el-button size="medium" type="primary" round @click="addPermission">新增权限</el-button>
      <el-input v-model="parameter.name" clearable size="medium" placeholder="搜索">
        <el-button slot="append" icon="el-icon-search" @click="getMenuPermissionsList" />
      </el-input>
    </template>

    <template slot="container">
      <el-table v-loading="loading" :data="permissions" size="medium" row-key="id" :style="{ width: `${tableWidth}%` }" :height="`${tableHeight}%`">
        <el-table-column prop="name" label="权限名称" />
        <el-table-column label="上级权限">
          <template v-slot="{ row }">
            {{ defaultTips(row.parentName) }}
          </template>
        </el-table-column>
        <el-table-column prop="slug" label="权限标识" />
        <el-table-column prop="path" label="链接地址" />
        <el-table-column label="权限类型">
          <template v-slot="{ row }">
            {{ permissionType(row.type) }}
          </template>
        </el-table-column>
        <el-table-column prop="sort" label="权重" />
        <el-table-column label="状态">
          <template v-slot="{ row }">
            <span v-if="row.status === 1" class="enable">启用</span>
            <span v-else-if="row.status === 2" class="disable">禁用</span>
            <span v-else>-</span>
          </template>
        </el-table-column>
        <el-table-column fixed="right" label="操作" width="100">
          <template v-slot="{ row }">
            <el-button type="text" size="medium" @click="() => editPermission(row)">编辑</el-button>
            <el-button type="text" size="medium" @click="() => deletePermission(row)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>
    </template>

    <template #footer-right>
      <el-pagination background :current-page="parameter.pageNumber" :page-sizes="[20, 50, 100, 200, 500]" :page-size="parameter.pageSize" layout="total, sizes, prev, pager, next, jumper" :total="total" class="el-pagination" @size-change="sizeChange" @current-change="currentChange" />
    </template>

    <!--新增/编辑权限对话框 -->
    <add-or-edit-dialog ref="addOrEditDialog" :title="title" @update="getMenuPermissionsList" />
  </page-skeleton>
</template>

<script>
import API from '@/api'
import PageSkeleton from '@/components/PageSkeleton.vue'
import AddOrEditDialog from './components/AddOrEditDialog.vue'

export default {
  name: 'user_permission',
  components: {
    'page-skeleton': PageSkeleton,
    'add-or-edit-dialog': AddOrEditDialog
  },
  data() {
    return {
      tableWidth: 100,
      tableHeight: 100,

      // 获取权限列表的请求参数
      parameter: {
        name: undefined, // 搜索关键字
        pageSize: 20,
        pageNumber: 1
      },
      permissions: [], // 权限列表
      total: 0, // 权限列表总数量
      loading: false, // 表格loading

      title: '新增' // 新增或编辑对话框标题
    }
  },
  computed: {
    permissionType() {
      return (type) => {
        switch (type) {
          case 1:
            return '菜单权限'
          case 2:
            return '功能权限'
          default:
            return '-'
        }
      }
    },
    defaultTips() {
      return (value) => value || '-'
    }
  },
  created() {
    this.getMenuPermissionsList()
  },
  methods: {
    /**
     * API 获取权限列表
     * @param {Boolean} reset 是否重置分页
     */
    async getMenuPermissionsList(reset = true) {
      try {
        if (reset) {
          this.parameter.pageNumber = 1
        }
        this.loading = true
        let { data } = await API.getMenuPermissionsList(this.parameter)
        this.total = data.total
        this.permissions = data.list
        this.loading = false
      } catch (error) {
        this.loading = false
        throw new Error(error.message)
      }
    },

    /**
     * 新增权限
     */
    addPermission() {
      this.title = '新增'
      this.$refs.addOrEditDialog.show()
    },

    /**
     * 编辑权限
     * @param {Object} row 当前编辑的行
     * @param {参数类型} 参数名称
     * @returns {返回类型} 返回值信息描述
     */
    editPermission(row) {
      this.title = '编辑'
      this.$refs.addOrEditDialog.show(row)
    },

    /**
     * 删除权限
     * @param {Object} row 当前删除的行
     */
    deletePermission(row) {
      this.$confirm('此操作将永久删除该权限, 是否继续?', '提示', {
        confirmButtonText: '确认',
        cancelButtonText: '取消',
        type: 'warning'
      })
        .then(async () => {
          let form = {
            parentId: row.parentId, // 父级ID
            name: row.name, // 权限名称
            type: row.type, // 1.菜单权限 2.功能权限
            path: row.path, // 菜单地址
            slug: row.slug, // 权限唯一标识
            slugAlias: row.slugAlias, // 权限唯一标识别名
            icon: row.icon, // 菜单Icon
            sort: row.sort, // 权重
            status: row.status, // 1.启用 2.禁用
            id: row.id, // 新增时不传(编辑时必传)
            ifDelete: 1 // 是否删除: 0.不删除 1.删除
          }
          let { code, message } = await API.addOrEditMenuPermissions(form)
          if (code === 10000) {
            this.$message.success(message)
            this.getMenuPermissionsList(false)
          } else {
            this.$message.error(message)
          }
        })
        .catch(() => {
          this.$message({
            type: 'info',
            message: '已取消删除'
          })
        })
    },

    /**
     * 每页显示条数发生改变时的回调
     * @param {Number} size 每页显示条数
     */
    sizeChange(size) {
      this.parameter.pageSize = size
      this.getMenuPermissionsList(false)
    },

    /**
     * 上一页/下一页/当前页发生改变时的回调
     * @param {Number} current 当前页
     */
    currentChange(current) {
      this.parameter.pageNumber = current
      this.getMenuPermissionsList(false)
    }
  }
}
</script>

<style lang="scss" scoped>
.el-input {
  width: 260px;
  /deep/.el-input__inner {
    border-top-left-radius: 18px;
    border-bottom-left-radius: 18px;
  }
  /deep/.el-input-group__append {
    border-top-right-radius: 18px !important;
    border-bottom-right-radius: 18px !important;
  }
}
.enable {
  color: #99c41c;
}

.disable {
  color: #f56c6c;
}
</style>
